import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Coins, Download, PhoneCall, PhoneIncoming, Target, Users, Voicemail } from "lucide-react";
import { get } from "@/lib/api";
import { KpiTile, PageHeader, Section } from "@/components/common/Layout";
import { DataTable } from "@/components/common/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money, pct } from "@/lib/format";

function tiraneToday(offsetDays = 0) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Tirane",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  const [year, month, day] = parts.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + offsetDays));
  return shifted.toISOString().slice(0, 10);
}

function csvCell(value) {
  const text = value == null ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadCsv(report) {
  const header = ["Campaign", "Calls", "Answered", "Voicemails", "Interested", "Callbacks", "Leads", "Spend"];
  const lines = [header.join(",")];
  const rows = [
    ...(report.campaigns || []),
    { name: "Total", ...report.totals },
  ];
  rows.forEach((row) => {
    lines.push([
      csvCell(row.name),
      row.calls ?? 0,
      row.answered ?? 0,
      row.voicemail ?? 0,
      row.interested ?? 0,
      row.callbacks ?? 0,
      row.leads ?? 0,
      Number(row.spend || 0).toFixed(2),
    ].join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `daily-report-${report.date_from}${report.date_to !== report.date_from ? `-to-${report.date_to}` : ""}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function DailyReport() {
  const today = useMemo(() => tiraneToday(), []);
  const [filters, setFilters] = useState({ date_from: today, date_to: today, campaign_id: "" });
  const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));

  const { data, isLoading, isError } = useQuery({
    queryKey: ["daily-report", params],
    queryFn: () => get("/reports/daily", params),
  });
  const { data: campaigns } = useQuery({
    queryKey: ["campaign-options"],
    queryFn: () => get("/campaigns", { page_size: 100 }),
  });

  const totals = data?.totals || {};
  const rows = (data?.campaigns || []).map((row) => ({ ...row, id: row.id || row.name }));
  const set = (key) => (value) => setFilters((current) => ({
    ...current,
    [key]: value === "all" ? "" : value,
  }));
  const applyRange = (fromOffset, toOffset = fromOffset) => {
    setFilters((current) => ({
      ...current,
      date_from: tiraneToday(fromOffset),
      date_to: tiraneToday(toOffset),
    }));
  };

  return (
    <>
      <PageHeader
        testId="daily-report-page"
        title="Daily report"
        subtitle="Live calls by calendar day in Europe/Tirane: placed, answered, voicemail, interested, callbacks, leads, and spend"
        actions={
          <Button
            data-testid="daily-report-download"
            variant="outline"
            className="h-9"
            disabled={!data}
            onClick={() => downloadCsv(data)}
          >
            <Download className="mr-2 h-4 w-4" />
            Download CSV
          </Button>
        }
      />

      <div className="panel flex flex-col gap-3 p-3 lg:flex-row lg:items-center">
        <div className="flex flex-wrap gap-2">
          <Button data-testid="report-today" type="button" variant="outline" className="h-9" onClick={() => applyRange(0)}>
            Today
          </Button>
          <Button data-testid="report-yesterday" type="button" variant="outline" className="h-9" onClick={() => applyRange(-1)}>
            Yesterday
          </Button>
          <Button data-testid="report-last-7" type="button" variant="outline" className="h-9" onClick={() => applyRange(-6, 0)}>
            Last 7 days
          </Button>
        </div>
        <Input
          data-testid="report-date-from"
          type="date"
          value={filters.date_from}
          onChange={(event) => set("date_from")(event.target.value)}
          className="h-9 lg:w-40"
        />
        <Input
          data-testid="report-date-to"
          type="date"
          value={filters.date_to}
          onChange={(event) => set("date_to")(event.target.value)}
          className="h-9 lg:w-40"
        />
        <Select value={filters.campaign_id || "all"} onValueChange={set("campaign_id")}>
          <SelectTrigger data-testid="report-campaign" className="h-9 lg:w-56">
            <SelectValue placeholder="Campaign" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All campaigns</SelectItem>
            {(campaigns?.items || []).map((campaign) => (
              <SelectItem key={campaign.id} value={campaign.id}>{campaign.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isError ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800" data-testid="daily-report-error">
          The report could not be loaded. Check the dates and try again.
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <KpiTile testId="report-calls" label="Calls made" value={isLoading ? "—" : (totals.calls ?? 0)} icon={PhoneCall} />
        <KpiTile
          testId="report-answered"
          label="Answered"
          value={isLoading ? "—" : (totals.answered ?? 0)}
          hint={isLoading ? null : `${pct(totals.answer_rate)} · ${totals.conversations ?? 0} conversations`}
          tone="success"
          icon={PhoneIncoming}
        />
        <KpiTile
          testId="report-voicemail"
          label="Voicemails"
          value={isLoading ? "—" : (totals.voicemail ?? 0)}
          hint={isLoading ? null : pct(totals.voicemail_rate)}
          tone="warning"
          icon={Voicemail}
        />
        <KpiTile
          testId="report-interested"
          label="Interested"
          value={isLoading ? "—" : (totals.interested ?? 0)}
          tone="success"
          icon={Target}
        />
        <KpiTile
          testId="report-callbacks"
          label="Callbacks"
          value={isLoading ? "—" : (totals.callbacks ?? 0)}
          icon={CalendarClock}
        />
        <KpiTile
          testId="report-leads"
          label="Leads"
          value={isLoading ? "—" : (totals.leads ?? 0)}
          tone="success"
          icon={Users}
        />
        <KpiTile
          testId="report-spend"
          label="Spend"
          value={isLoading ? "—" : money(totals.spend)}
          hint={isLoading ? null : `${money(totals.cost_per_call)} per call`}
          icon={Coins}
        />
      </div>

      {!isLoading && totals.brief_pickups ? (
        <p className="text-sm text-slate-500" data-testid="report-brief-note">
          {totals.brief_pickups.toLocaleString()} answered calls were brief pickups that ended as “customer did not answer”.
          They stay in Answered. Conversations are the pickups where someone stayed on the line.
        </p>
      ) : null}

      <Section
        title="By campaign"
        subtitle={data ? `${data.date_from}${data.date_to !== data.date_from ? ` to ${data.date_to}` : ""} · ${data.timezone}` : "Europe/Tirane"}
        testId="report-by-campaign"
      >
        <DataTable
          testId="report-campaign-table"
          loading={isLoading}
          rows={rows}
          empty={{ title: "No live calls in this range", description: "Choose another day or campaign." }}
          columns={[
            { key: "name", label: "Campaign", render: (row) => <span className="font-medium text-slate-800">{row.name}</span> },
            { key: "calls", label: "Calls" },
            { key: "answered", label: "Answered", render: (row) => `${row.answered} (${pct(row.answer_rate)})` },
            { key: "voicemail", label: "Voicemails" },
            { key: "interested", label: "Interested" },
            { key: "callbacks", label: "Callbacks" },
            { key: "leads", label: "Leads" },
            { key: "spend", label: "Spend", render: (row) => money(row.spend) },
          ]}
        />
      </Section>
    </>
  );
}
